import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Snowflake, ArrowLeft, ShieldAlert, CheckCircle2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GutscheinWiderruf = () => {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [confirmAccept, setConfirmAccept] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) {
      toast.error("Bitte Gutschein-Code und E-Mail-Adresse angeben.");
      return;
    }
    if (!confirmAccept) {
      toast.error("Bitte bestätige den Widerruf.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/gutschein/widerruf`, {
        code: code.trim().toUpperCase(),
        purchaser_email: email.trim(),
      });
      setResult(data);
      toast.success("Widerruf erfolgreich – Erstattung wurde veranlasst.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Widerruf fehlgeschlagen. Bitte später erneut versuchen.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-sky-100">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <Link to="/gutschein" className="inline-flex items-center gap-2 text-sky-700 hover:text-sky-900 mb-6" data-testid="widerruf-back-link">
          <ArrowLeft className="w-4 h-4" />
          Zurück zum Gutscheinkauf
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Snowflake className="w-7 h-7 text-sky-600" />
          <h1 className="text-3xl font-bold text-slate-900">Gutschein-Widerruf</h1>
        </div>
        <p className="text-slate-600 mb-8">
          Du hast das Recht, deinen Gutschein-Kauf binnen <strong>14 Tagen</strong> ohne Angabe von Gründen zu widerrufen –
          sofern noch <strong>kein Gutschein der Bestellung eingelöst</strong> wurde. Die Erstattung erfolgt automatisch über PayPal.
        </p>

        {!result ? (
          <Card className="shadow-lg border-sky-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <RotateCcw className="w-5 h-5 text-sky-600" />
                Widerruf erklären
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="code">Gutschein-Code</Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="GS-XXXX-XXXX"
                    className="font-mono uppercase"
                    autoComplete="off"
                    data-testid="widerruf-code-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Es genügt ein einzelner Code aus deiner Bestellung – bei Sammelkäufen wird die gesamte Bestellung widerrufen.
                  </p>
                </div>
                <div>
                  <Label htmlFor="email">E-Mail-Adresse des Käufers</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre@email.de"
                    autoComplete="email"
                    data-testid="widerruf-email-input"
                  />
                </div>

                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 flex gap-3 text-sm text-amber-900">
                  <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    Mit dem Widerruf werden <strong>alle Gutscheine</strong> dieser Bestellung sofort ungültig.
                    Die Erstattung erfolgt über PayPal an dasselbe Konto, mit dem bezahlt wurde
                    (1–5 Werktage Bearbeitungszeit).
                  </div>
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmAccept}
                    onChange={(e) => setConfirmAccept(e.target.checked)}
                    className="mt-1"
                    data-testid="widerruf-confirm-checkbox"
                  />
                  <span>
                    Ich erkläre hiermit den Widerruf des Gutschein-Kaufs und bestätige, dass ich die Hinweise gelesen habe.
                  </span>
                </label>

                <Button
                  type="submit"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                  disabled={loading}
                  data-testid="widerruf-submit-btn"
                >
                  {loading ? "Wird verarbeitet…" : "Widerruf jetzt absenden"}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-emerald-200 bg-emerald-50/40" data-testid="widerruf-success">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl text-emerald-700">
                <CheckCircle2 className="w-6 h-6" />
                Widerruf erfolgreich
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-slate-700">
              <p>
                Wir haben deinen Widerruf entgegengenommen und die Erstattung über PayPal veranlasst.
              </p>
              <ul className="list-disc list-inside text-sm bg-white rounded-md border border-emerald-200 p-3">
                <li><strong>Erstattungsbetrag:</strong> {Number(result.refund_amount).toFixed(2)} €</li>
                <li><strong>Anzahl Gutscheine:</strong> {result.vouchers_count}</li>
                <li><strong>PayPal Refund-ID:</strong> <span className="font-mono text-xs">{result.refund_id}</span></li>
              </ul>
              <p className="text-sm text-slate-600">
                Eine Bestätigung wurde an deine E-Mail-Adresse versendet. Die Gutschein-Codes wurden ungültig gestellt.
              </p>
              <Link to="/" className="inline-block">
                <Button variant="outline" className="mt-2" data-testid="widerruf-back-home-btn">
                  Zurück zur Startseite
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <p className="text-xs text-slate-500 mt-8">
          Rechtlicher Hinweis: Es gilt das gesetzliche Widerrufsrecht gemäß §§ 355 ff. BGB. Bereits eingelöste Gutscheine sind vom Widerruf ausgeschlossen.
          Nach Ablauf der 14-Tage-Frist wende dich bitte direkt an <a href="mailto:info@groemitzereiszeit.de" className="underline">info@groemitzereiszeit.de</a>.
        </p>
      </div>
    </div>
  );
};

export default GutscheinWiderruf;
